import * as React from 'react';

import {
  enumInventoryLevelSortOption,
  enumInventoryViewMode,
  enumLocationFilterType,
  InventoryLevelSortOption,
  InventoryViewMode,
} from '@rematter/frontend-lib-api-client';
import {
  DataGrid,
  ListScreenLayout,
  Pagination,
  SearchField,
  Select,
  Toggle,
  ToggleButtonGroup,
} from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/inventoryLevelsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLevelRecord } from '../../types/inventoryLevel';

import { useInventoryLevelsColumns } from './useInventoryLevelsColumns';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type ZoneOption = {
  id: string;
  name: string;
  zoneCode: string;
  zoneType: string;
  parentZoneId: string | null;
};

// Stable empty array to prevent re-renders
const EMPTY_ZONES: ZoneOption[] = [];

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: InventoryLevelSortOption;
    desc: InventoryLevelSortOption;
  }
> = {
  item: {
    asc: enumInventoryLevelSortOption.ItemNameAsc,
    desc: enumInventoryLevelSortOption.ItemNameDesc,
  },
  quantity: {
    asc: enumInventoryLevelSortOption.QuantityAsc,
    desc: enumInventoryLevelSortOption.QuantityDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ INVENTORY LEVELS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const InventoryLevelsListView = () => {
  const t = useTranslations();
  const [, setLocation] = useModuleLocation();

  const { data, isFetching, loadingState, error } = useAtomValue(
    state.$remoteInventoryLevels,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);
  const { data: zonesData } = useAtomValue(state.$remoteZonesForFacility);
  const [quantityFilter, setQuantityFilter] = useAtom(state.$quantityFilter);

  const [searchTerm, setSearchTerm] = useAtom(state.$searchTerm);
  const [selectedFacility, setSelectedFacility] = useAtom(
    state.$selectedFacility,
  );
  const [selectedZone, setSelectedZone] = useAtom(state.$selectedZone);
  const [viewMode, setViewMode] = useAtom(state.$viewMode);
  const [locationFilter, setLocationFilter] = useAtom(state.$locationFilter);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);

  const setCurrentSorting = useSetAtom(state.$currentSorting);

  const currentViewMode = (viewMode ||
    enumInventoryViewMode.OurMaterial) as InventoryViewMode;
  const allLevels = data?.levels ?? [];
  const levels =
    quantityFilter === 'negativeOnly'
      ? allLevels.filter((l) => l.quantity < 0)
      : allLevels;
  const total = data?.totalCount ?? 0;
  const emptyPanelProps = {
    title: t.inventoryLevelsView.emptyState.title,
    description: <span>{t.inventoryLevelsView.emptyState.subtitle}</span>,
    centered: true,
  };

  // Memoized values
  const facilities = React.useMemo(
    () => facilitiesData?.facilities ?? [],
    [facilitiesData?.facilities],
  );
  const zones = React.useMemo(
    () => (zonesData as ZoneOption[] | undefined) ?? EMPTY_ZONES,
    [zonesData],
  );
  const locationOptions = React.useMemo(() => {
    if (currentViewMode === enumInventoryViewMode.OurMaterial) {
      return [
        {
          key: enumLocationFilterType.AllLocations,
          label: t.inventoryLevelsView.filters.allLocations,
        },
        {
          key: enumLocationFilterType.OurFacilities,
          label: t.inventoryLevelsView.filters.ourFacilities,
        },
        {
          key: enumLocationFilterType.ExternalProcessors,
          label: t.inventoryLevelsView.filters.externalProcessors,
        },
        { key: 'divider', label: '—', disabled: true },
        ...facilities.map((f) => ({ key: f.id, label: f.name })),
      ];
    }
    return [
      { key: 'all', label: t.inventoryLevelsView.filters.allFacilities },
      ...facilities.map((f) => ({ key: f.id, label: f.name })),
    ];
  }, [currentViewMode, facilities, t]);
  const currentLocationValue = React.useMemo(() => {
    if (currentViewMode === enumInventoryViewMode.OurMaterial) {
      if (selectedFacility && selectedFacility !== 'all') {
        return selectedFacility;
      }
      return locationFilter || enumLocationFilterType.AllLocations;
    }
    return selectedFacility || 'all';
  }, [currentViewMode, locationFilter, selectedFacility]);
  const zoneOptions = React.useMemo(() => {
    if (!selectedFacility || selectedFacility === 'all' || zones.length === 0) {
      return [];
    }
    return [
      { key: 'all', label: t.inventoryLevelsView.filters.allZones },
      ...zones.map((z) => ({ key: z.id, label: `${z.name} (${z.zoneCode})` })),
    ];
  }, [selectedFacility, zones, t]);
  const showZoneDropdown = React.useMemo(
    () => selectedFacility && selectedFacility !== 'all' && zones.length > 0,
    [selectedFacility, zones],
  );
  // Callbacks
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumInventoryLevelSortOption.QuantityDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleRowClick = React.useCallback(
    ({ row }: { row: InventoryLevelRecord }) => {
      if (!row?.itemId) return;

      const query: Record<string, string> = {
        view: currentViewMode,
        name: row.itemName,
      };
      if (row.facilityId) query.fac = row.facilityId;
      else if (selectedFacility && selectedFacility !== 'all')
        query.fac = selectedFacility;
      if (selectedFacility && selectedFacility !== 'all')
        query.f = selectedFacility;

      setLocation('/inventory/levels/:itemId', {
        params: { itemId: row.itemId },
        query: Object.keys(query).length > 0 ? query : undefined,
      });
    },
    [currentViewMode, selectedFacility, setLocation],
  );
  const groupByFn = React.useCallback(
    (row: InventoryLevelRecord) => row.itemGroupId ?? '__ungrouped__',
    [],
  );
  const handleLocationChange = React.useCallback(
    (key: string) => {
      const isVirtualLocation =
        key === enumLocationFilterType.AllLocations ||
        key === enumLocationFilterType.ExternalProcessors ||
        key === enumLocationFilterType.OurFacilities;

      if (currentViewMode === enumInventoryViewMode.OurMaterial) {
        if (isVirtualLocation) {
          setLocationFilter(key);
          setSelectedFacility(undefined);
          setSelectedZone(undefined);
        } else if (key === 'divider') {
          return;
        } else {
          setLocationFilter(undefined);
          setSelectedFacility(key);
          setSelectedZone(undefined);
        }
      } else {
        setSelectedFacility(key === 'all' ? undefined : key);
        setSelectedZone(undefined);
      }
      setOffset(0);
    },
    [
      currentViewMode,
      setLocationFilter,
      setSelectedFacility,
      setSelectedZone,
      setOffset,
    ],
  );
  const handleZoneChange = React.useCallback(
    (key: string) => {
      setSelectedZone(key === 'all' ? undefined : key);
      setOffset(0);
    },
    [setSelectedZone, setOffset],
  );
  const handleViewModeChange = React.useCallback(
    (mode: string) => {
      setViewMode(mode);
      setOffset(0);
      setQuantityFilter('all');
      if (mode === enumInventoryViewMode.OurMaterial) {
        setLocationFilter(enumLocationFilterType.AllLocations);
        setSelectedFacility(undefined);
        setSelectedZone(undefined);
      } else {
        setLocationFilter(undefined);
        setSelectedFacility(undefined);
        setSelectedZone(undefined);
      }
    },
    [
      setViewMode,
      setOffset,
      setQuantityFilter,
      setLocationFilter,
      setSelectedFacility,
      setSelectedZone,
    ],
  );

  // Other data hooks
  const columns = useInventoryLevelsColumns({
    onRowClick: handleRowClick,
    viewMode: currentViewMode,
  });

  return (
    <ListScreenLayout
      filters={
        <>
          <ToggleButtonGroup
            className="@InventoryViewModeToggle"
            selectionMode="single"
            selectedKeys={[currentViewMode]}
            onSelectionChange={(v) =>
              handleViewModeChange(
                Array.from(v.values())[0] as InventoryViewMode,
              )
            }
          >
            <Toggle id={enumInventoryViewMode.OurMaterial}>
              {t.inventoryLevelsView.viewModes.ourMaterial}
            </Toggle>
            <Toggle id={enumInventoryViewMode.TollingMaterial}>
              {t.inventoryLevelsView.viewModes.tollingMaterial}
            </Toggle>
          </ToggleButtonGroup>

          <SearchField
            autoFocus
            value={searchTerm ?? ''}
            onChange={(value) => {
              setSearchTerm(value || undefined);
              setOffset(0);
            }}
            placeholder={t.inventoryLevelsView.filters.search}
            className="w-64"
          />

          <Select
            value={currentLocationValue}
            onChange={handleLocationChange}
            options={locationOptions}
            className="w-48"
          />

          {showZoneDropdown && (
            <Select
              value={selectedZone || 'all'}
              onChange={handleZoneChange}
              options={zoneOptions}
              className="w-48"
            />
          )}
        </>
      }
      footer={
        total > limit && (
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        )
      }
      className="@InventoryLevelsListView"
    >
      <DataGrid<InventoryLevelRecord>
        fullWidth
        isLoading={loadingState.isInitialLoading || isFetching}
        columns={columns}
        dataSource={levels}
        rowKey="id"
        aria-label={t.inventoryLevelsView.table.aria}
        onColumnSort={handleColumnSort}
        groupBy={groupByFn}
        className="w-full cursor-pointer text-sm"
        hasError={!!error}
        errorPanelProps={
          error
            ? {
                description: t.inventoryLevelsView.content.error(error.message),
                centered: true,
              }
            : undefined
        }
        emptyPanelProps={emptyPanelProps}
      />
    </ListScreenLayout>
  );
};
